import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Check, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { tk } from "@/i18n";
import { fmtMoney, fmtNumber } from "@/i18n/format";
import { api, errorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useCatalog } from "@/components/CreditPacks";

const PLANS = [
  { id: "monthly", name: tk("Monthly plan"), amount: 90, per: tk("excl. VAT / user / month"), note: tk("Billed monthly, cancel anytime"), cta: tk("Choose the monthly plan") },
  { id: "annual", name: tk("Annual plan"), amount: 690, per: tk("excl. VAT / user / year"), note: tk("Save about 36% with the annual plan"), cta: tk("Choose the annual plan"), best: true },
];

function PlanCard({ p, canBuy, loading, onPick, catalog }) {
  const { t } = useI18n();
  const price = catalog?.plans.find((x) => x.id === p.id);
  return (
    <div className={`rounded-xl border p-4 flex flex-col ${p.best ? "border-primary ai-ring" : ""}`} data-testid={`upgrade-plan-${p.id}`}>
      <div className="eyebrow">{t(p.name)}</div>
      <div className="mt-1 text-2xl font-bold" data-testid={`upgrade-price-${p.id}`}>{fmtMoney(price ? price.price : p.amount, catalog?.currency || "EUR")}<span className="text-sm font-medium text-muted-foreground"> {t(p.per)}</span></div>
      <div className="text-xs text-muted-foreground mt-1">{t("{{credits}} AI credits per seat / month", { credits: fmtNumber(price?.credits_per_seat_month ?? (p.id === "annual" ? 1150 : 1800)) })}</div>
      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" />{t(p.note)}</div>
      <Button className="w-full mt-4" variant={p.best ? "default" : "outline"} disabled={!canBuy || !!loading} onClick={() => onPick(p.id)} data-testid={`upgrade-checkout-${p.id}`}>
        {loading === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : t(p.cta)}
      </Button>
    </div>
  );
}

export function UpgradeModal() {
  const { upgradeOpen, setUpgradeOpen, user } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(null);
  const [catalog] = useCatalog();
  const role = user?.memberships?.[0]?.role;
  const canBuy = role === "owner" || role === "admin";

  const checkout = async (plan) => {
    setLoading(plan);
    try {
      const { data } = await api.post("/billing/checkout", { plan, seats: 1, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errorDetail(e));
      setLoading(null);
    }
  };

  return (
    <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
      <DialogContent className="max-w-lg" data-testid="upgrade-modal">
        <DialogHeader>
          <div className="w-10 h-10 rounded-lg ai-gradient flex items-center justify-center mb-2"><Lock className="w-5 h-5 text-white" /></div>
          <DialogTitle className="text-xl" data-testid="upgrade-modal-title">{t("Unlock all TOPTALENT features")}</DialogTitle>
          <DialogDescription data-testid="upgrade-modal-description">
            {t("Access available professional contact details, contact candidates and use our AI-assisted recruiting tools.")}{" "}
            {t("Prices exclude applicable taxes. A paid plan never overrides candidate privacy or source restrictions.")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          {PLANS.map((p) => <PlanCard key={p.id} p={p} canBuy={canBuy} loading={loading} onPick={checkout} catalog={catalog} />)}
        </div>
        {!canBuy && <p className="text-xs text-muted-foreground" data-testid="upgrade-role-note">{t("Ask an organization owner or admin to subscribe.")}</p>}
        <div className="flex justify-end"><Button variant="ghost" onClick={() => setUpgradeOpen(false)} data-testid="upgrade-cancel">{t("Cancel")}</Button></div>
      </DialogContent>
    </Dialog>
  );
}
